import React, { useState, useEffect } from 'react';
import './Land.css';

const BACKEND_URL = 'https://web3-pos-v2.gdbok.workers.dev';

const TRANSLATIONS = {
  1: {
    title: 'Historial de Transacciones',
    today: 'Hoy',
    yesterday: 'Ayer',
    last7Days: 'Últimos 7 Días',
    custom: 'Personalizado',
    from: 'Desde',
    to: 'Hasta',
    apply: 'Aplicar',
    backToPOS: 'Volver al POS',
    noTransactions: 'No hay transacciones',
    noTransactionsDesc: 'No se encontraron transacciones para el período seleccionado',
    loading: 'Cargando...',
    error: 'Error al cargar historial',
    mainRevenue: 'Ingreso Principal',
    periodRevenue: 'Ingreso del Período',
    transactions: 'transacciones',
    transaction: 'transacción',
    otherChains: 'otras redes',
    otherChain: 'otra red',
    tapToSeeBreakdown: 'Toca para ver desglose',
    on: 'ref',
    breakdown: 'Desglose Detallado',
    byChain: 'Por Red'
  },
  2: {
    title: 'Transaction History',
    today: 'Today',
    yesterday: 'Yesterday',
    last7Days: 'Last 7 Days',
    custom: 'Custom',
    from: 'From',
    to: 'To',
    apply: 'Apply',
    backToPOS: 'Back to POS',
    noTransactions: 'No transactions',
    noTransactionsDesc: 'No transactions found for the selected period',
    loading: 'Loading...',
    error: 'Error loading history',
    mainRevenue: 'Main Revenue',
    periodRevenue: 'Period Revenue',
    transactions: 'transactions',
    transaction: 'transaction',
    otherChains: 'other chains',
    otherChain: 'other chain',
    tapToSeeBreakdown: 'Tap to see breakdown',
    on: 'ref',
    breakdown: 'Detailed Breakdown',
    byChain: 'By Chain'
  }
};

function HistoryScreen({ tokenId, genericToken, lang, onClose }) {
  const t = TRANSLATIONS[lang];
  
  const [dateFilter, setDateFilter] = useState('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showBreakdown, setShowBreakdown] = useState(false);

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter]);

  const getDateRange = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    const formatDate = (date) => date.toISOString().split('T')[0];

    switch (dateFilter) {
      case 'today':
        return { from: formatDate(today), to: formatDate(today) };
      case 'yesterday':
        return { from: formatDate(yesterday), to: formatDate(yesterday) };
      case 'last7Days':
        return { from: formatDate(weekAgo), to: formatDate(today) };
      case 'custom':
        return { from: customFrom, to: customTo };
      default:
        return { from: formatDate(today), to: formatDate(today) };
    }
  };

  const loadHistory = async () => {
    setLoading(true);
    setError('');

    const { from, to } = getDateRange();
    
    if (!from || !to) {
      setLoading(false);
      return;
    }

    try {
      // ✅ Load transactions for display
      const historyResponse = await fetch(
        `${BACKEND_URL}/api/token/${tokenId}/history?from=${from}&to=${to}&limit=100`
      );
      
      if (!historyResponse.ok) {
        throw new Error('Failed to fetch history');
      }

      const historyData = await historyResponse.json();
      setTransactions(historyData.payments || []);

      // ✅ Load and aggregate stats for ALL dates in the range
      const startDate = new Date(from);
      const endDate = new Date(to);
      const dates = [];
      
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        dates.push(d.toISOString().split('T')[0]);
      }

      // ✅ Aggregate stats from all dates
      const aggregatedStats = {
        totalTransactions: 0,
        totalAmount: '0.00',
        totalFiatAmount: null,
        fiatCurrency: null,
        byChainAndToken: {},
        byChain: {}
      };

      for (const date of dates) {
        const statsResponse = await fetch(
          `${BACKEND_URL}/api/token/${tokenId}/stats?date=${date}`
        );
        
        if (statsResponse.ok) {
          const dayStats = await statsResponse.json();

          if (dayStats.totalTransactions > 0) {
            aggregatedStats.totalTransactions += dayStats.totalTransactions;
        
            // ✅ NEW: Aggregate fiat totals
            if (dayStats.totalFiatAmount) {
              if (!aggregatedStats.totalFiatAmount) {
                aggregatedStats.totalFiatAmount = '0.00';
                aggregatedStats.fiatCurrency = dayStats.fiatCurrency;
              }
              const currentFiat = parseFloat(aggregatedStats.totalFiatAmount || '0');
              const additionFiat = parseFloat(dayStats.totalFiatAmount || '0');
              aggregatedStats.totalFiatAmount = (currentFiat + additionFiat).toFixed(2);
            }

            // ✅ Aggregate byChainAndToken
            if (dayStats.byChainAndToken) {
              Object.entries(dayStats.byChainAndToken).forEach(([key, data]) => {
                if (!aggregatedStats.byChainAndToken[key]) {
                  aggregatedStats.byChainAndToken[key] = {
                    receiveChain: data.receiveChain,
                    chainName: data.chainName,
                    count: 0,
                    totalAmount: '0.00',
                    totalFiatAmount: data.fiatCurrency ? '0.00' : null,
                    fiatCurrency: data.fiatCurrency || null
                  };
                }
                aggregatedStats.byChainAndToken[key].count += data.count;
                
                // Aggregate crypto amounts
                const current = parseFloat(aggregatedStats.byChainAndToken[key].totalAmount || '0');
                const addition = parseFloat(data.totalAmount || '0');
                aggregatedStats.byChainAndToken[key].totalAmount = (current + addition).toFixed(2);
                
                // ✅ NEW: Aggregate fiat amounts per chain
                if (data.totalFiatAmount && data.fiatCurrency) {
                  const currentFiat = parseFloat(aggregatedStats.byChainAndToken[key].totalFiatAmount || '0');
                  const additionFiat = parseFloat(data.totalFiatAmount || '0');
                  aggregatedStats.byChainAndToken[key].totalFiatAmount = (currentFiat + additionFiat).toFixed(2);
                  aggregatedStats.byChainAndToken[key].fiatCurrency = data.fiatCurrency;
                }
              });
            }
          }
        }
      }

      // ✅ Calculate total amount from byChainAndToken (ONCE - not twice)
      aggregatedStats.totalAmount = Object.values(aggregatedStats.byChainAndToken)
        .reduce((sum, data) => sum + parseFloat(data.totalAmount || '0'), 0)
        .toFixed(2);

      // ✅ Build byChain from byChainAndToken (ONCE - not twice)
      Object.entries(aggregatedStats.byChainAndToken).forEach(([key, data]) => {
        const chainId = data.receiveChain;
        if (!aggregatedStats.byChain[chainId]) {
          aggregatedStats.byChain[chainId] = {
            chainId,
            chainName: data.chainName,
            count: 0,
            totalAmount: '0.00',
            totalFiatAmount: data.fiatCurrency ? '0.00' : null,
            fiatCurrency: data.fiatCurrency || null
          };
        }
        aggregatedStats.byChain[chainId].count += data.count;
        
        // Aggregate crypto amounts
        const current = parseFloat(aggregatedStats.byChain[chainId].totalAmount || '0');
        const addition = parseFloat(data.totalAmount || '0');
        aggregatedStats.byChain[chainId].totalAmount = (current + addition).toFixed(2);
        
        // ✅ NEW: Aggregate fiat amounts per chain
        if (data.totalFiatAmount && data.fiatCurrency) {
          const currentFiat = parseFloat(aggregatedStats.byChain[chainId].totalFiatAmount || '0');
          const additionFiat = parseFloat(data.totalFiatAmount || '0');
          aggregatedStats.byChain[chainId].totalFiatAmount = (currentFiat + additionFiat).toFixed(2);
          aggregatedStats.byChain[chainId].fiatCurrency = data.fiatCurrency;
        }
      });

      setStats(aggregatedStats);

    } catch (err) {
      console.error('Error loading history:', err);
      setError(t.error);
    } finally {
      setLoading(false);
    }
  };

  const applyCustomDates = () => {
    if (!customFrom || !customTo) {
      setError(lang === 1 ? 'Seleccione ambas fechas' : 'Select both dates');
      return;
    }
    
    if (customFrom > customTo) {
      setError(lang === 1 ? 'Fecha inicial debe ser anterior a fecha final' : 'Start date must be before end date');
      return;
    }

    setError('');
    loadHistory();
  };

  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const isToday = date.toDateString() === today.toDateString();
    const isYesterday = date.toDateString() === yesterday.toDateString();

    const time = date.toLocaleTimeString(lang === 1 ? 'es-ES' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });

    if (isToday) {
      return `${t.today}, ${time}`;
    } else if (isYesterday) {
      return `${t.yesterday}, ${time}`;
    } else {
      const dateStr = date.toLocaleDateString(lang === 1 ? 'es-ES' : 'en-US', {
        month: 'short',
        day: 'numeric'
      });
      return `${dateStr}, ${time}`;
    }
  };

  // ✅ SIMPLIFIED: Use stats API data directly
  const formatAmount = (decimalAmount) => {
    try {
      return parseFloat(decimalAmount).toFixed(2);
    } catch (e) {
      return '0.00';
    }
  };

  const getStatsData = () => {
    if (!stats) {
      return {
        totalAmount: '0.00',
        totalFiatAmount: null,
        fiatCurrency: null,
        totalTransactions: 0,
        byChain: {}
      };
    }

    return {
      totalAmount: stats.totalAmount || '0.00',
      totalFiatAmount: stats.totalFiatAmount || null,
      fiatCurrency: stats.fiatCurrency || null,
      totalTransactions: stats.totalTransactions || 0,
      byChain: stats.byChain || {}
    };
  };

  const statsData = getStatsData();
  const chainCount = Object.keys(statsData.byChain).length;
  const otherChainsCount = Math.max(0, chainCount - 1);

  return (
    <div className="history-screen">
      <div className="history-header">
        <button className="history-back-btn" onClick={onClose}>
          ←
        </button>
        <h2 className="history-title">{t.title}</h2>
      </div>

      <div className="history-content">
        <div className="date-filters">
          <button
            className={`filter-btn ${dateFilter === 'today' ? 'active' : ''}`}
            onClick={() => setDateFilter('today')}
          >
            {t.today}
          </button>
          <button
            className={`filter-btn ${dateFilter === 'yesterday' ? 'active' : ''}`}
            onClick={() => setDateFilter('yesterday')}
          >
            {t.yesterday}
          </button>
          <button
            className={`filter-btn ${dateFilter === 'last7Days' ? 'active' : ''}`}
            onClick={() => setDateFilter('last7Days')}
          >
            {t.last7Days}
          </button>
          <button
            className={`filter-btn ${dateFilter === 'custom' ? 'active' : ''}`}
            onClick={() => setDateFilter('custom')}
          >
            {t.custom}
          </button>
        </div>

        {dateFilter === 'custom' && (
          <div className="custom-date-range">
            <div className="date-input-group">
              <label>{t.from}</label>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="date-input"
              />
            </div>
            <div className="date-input-group">
              <label>{t.to}</label>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="date-input"
              />
            </div>
            <button className="apply-btn" onClick={applyCustomDates}>
              {t.apply}
            </button>
          </div>
        )}

        {error && (
          <div className="alert" style={{ margin: '12px 0' }}>
            <span>⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {loading && (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>{t.loading}</p>
          </div>
        )}

        {!loading && transactions.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">🔭</div>
            <h3>{t.noTransactions}</h3>
            <p>{t.noTransactionsDesc}</p>
          </div>
        )}

        {!loading && transactions.length > 0 && (
          <div className="transactions-list">
            {transactions.map((tx, index) => (
              <div key={index} className="transaction-card">
                <div className="tx-card-header">
                  <div className="tx-amount-section">
                    {/* ✅ PRIMARY: Fiat Amount */}
                    {tx.fiatAmount && tx.fiatCurrency ? (
                      <>
                        <span className="tx-amount">
                          {tx.fiatCurrency === 'EUR' ? '€' : '$'}{formatAmount(tx.fiatAmount)}
                        </span>
                        <span className="tx-token">{tx.fiatCurrency}</span>
                      </>
                    ) : (
                      <>
                        <span className="tx-amount">${formatAmount(tx.decimalAmount)}</span>
                        <span className="tx-token">{tx.genericToken || genericToken}</span>
                      </>
                    )}
                  </div>
                  <span className="status-badge status-confirmed">✓</span>
                </div>
                <div className="tx-card-body">
                  <div className="tx-detail-row">
                    <span className="tx-chain-name">
                      {tx.chainName || 'Unknown'}
                    </span>
                    <span className="tx-datetime">{formatDateTime(tx.confirmedAt)}</span>
                  </div>
                  
                  {/* ✅ NEW: Show crypto amount and conversion rate */}
                  {tx.fiatAmount && tx.decimalAmount && (
                    <div style={{
                      fontSize: '11px',
                      color: '#9ca3af',
                      marginTop: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>≈ {formatAmount(tx.decimalAmount)} {tx.genericToken || genericToken}</span>
                      {tx.conversionRateString && (
                        <span style={{ opacity: 0.7 }}>
                          @ {parseFloat(tx.conversionRateString).toFixed(2)}
                        </span>
                      )}
                    </div>
                  )}
                  
                  <div className="tx-ref">{t.on} {tx.txRef}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && transactions.length > 0 && (
          <div className="history-summary">
            <div className="summary-header" onClick={() => setShowBreakdown(!showBreakdown)}>
              <div className="summary-main-revenue">
                <span className="summary-label">
                  {dateFilter === 'today' ? t.mainRevenue : t.periodRevenue}
                </span>
                
                {/* ✅ Check if we have fiat totals */}
                {statsData.totalFiatAmount && statsData.fiatCurrency ? (
                  <div className="summary-amount-group">
                    <span className="summary-token-badge">
                      {statsData.fiatCurrency}
                    </span>
                    <span className="summary-amount">
                      {statsData.fiatCurrency === 'EUR' ? '€' : '$'}{formatAmount(statsData.totalFiatAmount)}
                    </span>
                    {/* ✅ Show crypto amount below */}
                    <div style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      marginTop: '4px',
                      fontWeight: '400'
                    }}>
                      ≈ ${formatAmount(statsData.totalAmount)} {genericToken}
                    </div>
                  </div>
                ) : (
                  <div className="summary-amount-group">
                    <span className="summary-token-badge">
                      {genericToken}
                    </span>
                    <span className="summary-amount">${formatAmount(statsData.totalAmount)}</span>
                  </div>
                )}
              </div>

              <div className="summary-footer">
                <span className="summary-count">
                  {statsData.totalTransactions} {statsData.totalTransactions === 1 ? t.transaction : t.transactions}
                </span>
                {otherChainsCount > 0 && (
                  <span className="summary-other-tokens">
                    + {otherChainsCount} {otherChainsCount === 1 ? t.otherChain : t.otherChains}
                  </span>
                )}
              </div>
              
              <div className="summary-tap-hint">
                {t.tapToSeeBreakdown} {showBreakdown ? '▲' : '▼'}
              </div>
            </div>

            {showBreakdown && Object.keys(statsData.byChain).length > 0 && (
              <div className="breakdown-section">
                <div className="breakdown-title">{t.breakdown}</div>

                <div className="breakdown-group">
                  <div className="breakdown-subtitle">{t.byChain}</div>
                  {Object.entries(statsData.byChain)
                    .sort((a, b) => parseFloat(b[1].totalAmount) - parseFloat(a[1].totalAmount))
                    .map(([chainId, data], idx) => (
                      <div key={idx} className="breakdown-item">
                        <span className="breakdown-chain-token">
                          {data.chainName} - {genericToken}
                        </span>
                        <span className="breakdown-amount-count">
                          <div style={{ textAlign: 'right' }}>
                            {/* ✅ Show fiat amount per chain if available */}
                            {data.totalFiatAmount && data.fiatCurrency ? (
                              <>
                                <div className="breakdown-value">
                                  {data.fiatCurrency === 'EUR' ? '€' : '$'}{formatAmount(data.totalFiatAmount)}
                                </div>
                                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                                  ≈ {formatAmount(data.totalAmount)} {genericToken}
                                </div>
                              </>
                            ) : (
                              <span className="breakdown-value">${formatAmount(data.totalAmount)}</span>
                            )}
                            <span className="breakdown-count">({data.count})</span>
                          </div>
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default HistoryScreen;